import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as React from "react";
import { FaBan, FaExclamationTriangle, FaQuestionCircle } from "react-icons/fa";
import { FiCheck } from "react-icons/fi";

import { cn } from "@/lib/utils";

// const Dialog = (props: ModalProps) => {
//   const { type, children } = props;
//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <motion.div
//         initial={{ scale: 0.9, opacity: 0 }}
//         animate={{ scale: 1, opacity: 1 }}
//         transition={{ duration: 0.3 }}
//         className="bg-bg p-8 rounded-lg shadow-lg max-w-md w-full mx-4 border-2 border-ring"
//       >
//         <IconComponent iconType={type} />
//         {children}
//       </motion.div>
//     </div>
//   );
// };

const Dialog = DialogPrimitive.Root;

// Extend the props type to include className and ref
// type DialogProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
//   className?: string; // Add className as an optional prop
// };

// const Dialog = React.forwardRef<
//   React.ElementRef<typeof DialogPrimitive.Root>,
//   DialogProps // Use the extended DialogProps type
// >(({ className, ...props }, ref) => (
//   <DialogPrimitive.Root
//     ref={ref} // Ensure ref is correctly passed
//     className={cn("custom-dialog-class", className)} // Apply custom class names
//     {...props}
//   >
//     {/* You can include DialogTrigger, DialogContent, etc. here */}
//   </DialogPrimitive.Root>
// ));

Dialog.displayName = "Dialog"; // Set a display name for debugging

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[80] bg-black/50  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Define a new interface that extends the original props and includes the 'type' prop
interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  type?: "forbidden" | "success" | "danger" | "question";
  closeButton?: boolean;
  overlayClassName?: string;
}

// w-[calc(100vw-40px)]
// md:w-[calc(100vw-100px)]
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps // Use the new interface here
>(
  (
    {
      className,
      children,
      type,
      closeButton = false,
      overlayClassName,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          `bg-white 
        z-[90]

      fixed
      top-[50%]
      translate-x-[-50%]
      translate-y-[-50%]
      left-[50%]
      md:left-[calc(50%+30px)]
      w-[calc(100vw-40px)]  
      max-w-md    

        grid 
        gap-4 
        border 
        p-6
        sm:p-8 
        shadow-lg 
        duration-200 
        data-[state=open]:animate-in 
        data-[state=closed]:animate-out 
        data-[state=closed]:fade-out-0 
        data-[state=open]:fade-in-0 
        data-[state=closed]:zoom-out-95 
        data-[state=open]:zoom-in-95 
        data-[state=closed]:slide-out-to-left-1/2 
        data-[state=closed]:slide-out-to-top-[48%] 
        data-[state=open]:slide-in-from-left-1/2 
        data-[state=open]:slide-in-from-top-[48%] rounded-lg`,
          className,
        )}
        style={{
          isolation: "auto",
          // isolation: "isolate",
          position: "fixed",
          zIndex: 90,
        }}
        {...props}
      >
        <IconComponent iconType={type} />
        {children}
        {closeButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-xl font-bold text-center leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-fg text-center", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex justify-end gap-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const IconComponent: React.FC<{
  iconType?: "success" | "danger" | "forbidden" | "question";
}> = ({ iconType }) => {
  const iconMap = {
    success: <FiCheck className="text-green-500" size={40} />,
    forbidden: <FaBan className="text-red-500" />,
    danger: <FaExclamationTriangle className="text-orange-500" size={20} />,
    question: <FaQuestionCircle className="text-blue-500" />,
  };

  return (
    <>
      {iconType && (
        <div className="flex justify-center mb-2">
          <div className="bg-white rounded-full flex items-center justify-center">
            {iconMap[iconType as keyof typeof iconMap]}
          </div>
        </div>
      )}
    </>
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
